import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, categoryTabs } from '@/mocks/categories';

const categoryPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: '产品服务 - MJHL 产品矩阵',
  description: '浏览MJHL全系列产品与服务，涵盖智能家居、企业服务、健康科技、教育科技四大领域。',
  url: 'https://mjhl.com/categories',
};

export default function Categories() {
  const location = useLocation();
  const initialTab = (location.state as { tab?: string })?.tab || categoryTabs[0].id;
  const [activeTab, setActiveTab] = useState(initialTab);
  const [fadeKey, setFadeKey] = useState(initialTab);
  const [fade, setFade] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleTabChange = (tabId: string) => {
    if (tabId === activeTab) return;
    setFade(true);
    setTimeout(() => {
      setActiveTab(tabId);
      setFadeKey(tabId);
      setFade(false);
    }, 200);
  };

  const activeCategory = categories.find((c) => c.id === activeTab);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryPageSchema) }}
      />
      <main className="min-h-screen bg-background-50">
        <div className="pt-20">
          <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '产品服务' }]} />

          <section className="w-full px-4 md:px-6 py-8 md:py-12">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-4xl font-bold text-foreground-950">
                  产品与服务
                </h1>
                <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                  覆盖智能家居、企业服务、健康科技、教育科技四大领域，为每一位用户提供卓越体验
                </p>
              </div>

              <div className="flex items-center justify-center gap-2 mb-10 overflow-x-auto flex-nowrap pb-2">
                <div className="inline-flex items-center bg-background-100 rounded-full px-1 py-1">
                  {categoryTabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => handleTabChange(tab.id)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'bg-primary-500 text-white shadow-sm'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      <i className={`${tab.icon} text-sm`}></i>
                      {tab.name}
                    </button>
                  ))}
                </div>
              </div>

              <div
                key={fadeKey}
                className={`transition-opacity duration-200 ${fade ? 'opacity-0' : 'opacity-100'}`}
              >
                {activeCategory && (
                  <>
                    <div className="mb-8 text-center">
                      <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                        {activeCategory.description}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-product-shop>
                      {activeCategory.products.map((product) => (
                        <div
                          key={product.id}
                          className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-300 group"
                        >
                          <div className="relative overflow-hidden h-48">
                            <img
                              src={product.image}
                              alt={product.name}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          <div className="p-5">
                            <h3 className="text-base font-semibold text-foreground-950">
                              {product.name}
                            </h3>
                            <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                              {product.description}
                            </p>
                            <div className="mt-4 flex flex-wrap gap-1.5">
                              {product.features.map((f, i) => (
                                <span
                                  key={i}
                                  className="px-2.5 py-1 text-xs rounded-full bg-accent-100 text-accent-700 font-medium whitespace-nowrap"
                                >
                                  {f}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}